const BASE = '/api';

async function request(path: string, options: RequestInit = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error(err.error || 'Request failed'), { status: res.status, data: err });
  }
  return res.status === 204 ? null : res.json();
}

const qs = (params: Record<string, string>) =>
  Object.keys(params).length ? '?' + new URLSearchParams(params) : '';

export const api = {
  // profile / roles
  getProfile: () => request('/profile'),
  updateProfileLang: (lang: string) => request('/profile/lang', { method: 'PUT', body: JSON.stringify({ lang }) }),
  getMyRole: () => request('/roles/me'),
  getUserRoles: () => request('/roles'),
  setUserRole: (userId: number, role: string) => request(`/roles/${userId}`, { method: 'PUT', body: JSON.stringify({ role }) }),

  // the répertoire
  getCatalogue: () => request('/catalogue'),
  getRepoStatus: () => request('/repo/status'),
  getRepoIndex: () => request('/repo/index'),
  refreshRepoIndex: () => request('/repo/index/refresh', { method: 'POST' }),
  getRepoFile: (path: string, ref?: string) => request(`/repo/file?path=${encodeURIComponent(path)}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`),
  getRepoHistory: (path: string) => request(`/repo/history?path=${encodeURIComponent(path)}`),
  commitRepoFile: (body: { path: string; content: string; message?: string; sha?: string | null; process_id?: number }) =>
    request('/repo/file', { method: 'PUT', body: JSON.stringify(body) }),
  linkRepoFile: (process_id: number, git_path: string) =>
    request('/repo/link', { method: 'POST', body: JSON.stringify({ process_id, git_path }) }),

  // github — my linked account, and (admin) the app itself
  getGithubMe: () => request('/github/me'),
  recheckGithub: () => request('/github/me/recheck', { method: 'POST' }),
  unlinkGithub: () => request('/github/me', { method: 'DELETE' }),
  getGithubApp: () => request('/github/app'),
  getGithubManifest: () => request('/github/app/manifest'),
  refreshGithubApp: () => request('/github/app/refresh', { method: 'POST' }),
  forgetGithubApp: () => request('/github/app', { method: 'DELETE' }),
  getGithubLinks: () => request('/github/app/links'),

  // processes
  getProcesses: () => request('/processes'),
  getProcess: (id: number) => request(`/processes/${id}`),
  getPhases: () => request('/processes/phases'),
  createProcess: (body: any) => request('/processes', { method: 'POST', body: JSON.stringify(body) }),
  updateProcess: (id: number, body: any) => request(`/processes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  adoptFile: (git_path: string, name?: string) =>
    request('/processes/adopt', { method: 'POST', body: JSON.stringify({ git_path, name }) }),
  setCheck: (id: number, body: { phase_key: string; check_key: string; checked: boolean; note?: string }) =>
    request(`/processes/${id}/checks`, { method: 'POST', body: JSON.stringify(body) }),
  movePhase: (id: number, to_phase_key: string, reason?: string) =>
    request(`/processes/${id}/phase`, { method: 'POST', body: JSON.stringify({ to_phase_key, reason }) }),
  getRoles: (id: number) => request(`/processes/${id}/roles`),
  updateRole: (id: number, roleId: number, body: any) =>
    request(`/processes/${id}/roles/${roleId}`, { method: 'PUT', body: JSON.stringify(body) }),

  addSeance: (id: number, body: any) => request(`/processes/${id}/notes`, { method: 'POST', body: JSON.stringify(body) }),
  deleteSeance: (id: number, noteId: number) => request(`/processes/${id}/notes/${noteId}`, { method: 'DELETE' }),

  // mon travail
  getMyWork: () => request('/mywork'),
  takeCharge: (processId: number) => request(`/mywork/take/${processId}`, { method: 'POST' }),

  // gouvernance
  getGovernancePending: () => request('/governance/pending'),
  getByFunction: () => request('/governance/by-function'),
  getByOwner: () => request('/governance/by-owner'),
  getInventory: () => request('/governance/inventory'),

  // kpis / dashboards
  getKpis: (params: Record<string, string> = {}) => request(`/kpis${qs(params)}`),
  createKpi: (body: any) => request('/kpis', { method: 'POST', body: JSON.stringify(body) }),
  linkKpi: (kpiId: number, processId: number) =>
    request(`/kpis/${kpiId}/link`, { method: 'POST', body: JSON.stringify({ process_id: processId }) }),
  getDashboards: () => request('/kpis/dashboards/all'),
  createDashboard: (body: any) => request('/kpis/dashboards', { method: 'POST', body: JSON.stringify(body) }),

  // tickets
  getTickets: (params: Record<string, string> = {}) => request(`/tickets${qs(params)}`),
  getTicket: (id: number) => request(`/tickets/${id}`),
  createTicket: (body: any) => request('/tickets', { method: 'POST', body: JSON.stringify(body) }),
  updateTicket: (id: number, body: any) => request(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  commentTicket: (id: number, body: string) =>
    request(`/tickets/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  spawnRework: (ticketId: number) => request(`/tickets/${ticketId}/spawn-rework`, { method: 'POST' }),

  // lookups
  getPeople: () => request('/lookups/people'),
  createPerson: (name: string, extra: Record<string, any> = {}) =>
    request('/lookups/people', { method: 'POST', body: JSON.stringify({ name, ...extra }) }),
  getFunctions: () => request('/lookups/functions'),
  createFunction: (name: string) => request('/lookups/functions', { method: 'POST', body: JSON.stringify({ name }) }),
};
